import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import ZoomableImage from './ZoomableImage';

type GalleryItem = {
  src: string;
  label?: string;
};

type Props = {
  src: string;
  alt?: string;
  onClose: () => void;
  /** Optional: called with the rotated image base64 when user clicks the
   *  save-rotation button. If omitted, rotation is view-only. */
  onSaveRotation?: (rotatedBase64: string) => void | Promise<void>;
  /** Optional right-side info panel — e.g. classification of this image
   *  ("CCCD mặt trước") + extracted fields for side-by-side verification. */
  infoPanel?: React.ReactNode;
  /** Optional gallery list — when provided, shows prev/next arrows so the
   *  operator can walk through all images of the same student (CCCD front,
   *  back, GPLX, portrait, …) without closing the overlay. `src` is used
   *  as the initial image only; internal state tracks the current index. */
  gallery?: GalleryItem[];
  /** Fires whenever the active gallery item changes (open, prev, next).
   *  Parent can use this to cross-highlight the matching doc section in
   *  a side info panel — e.g. when a GPLX image is on screen, pink-ring
   *  the GPLX form fields so the operator's eye tracks between them. */
  onActiveChange?: (item: GalleryItem) => void;
};

/**
 * Full-viewport image zoom overlay.
 *
 * Rendered via React Portal into document.body so it escapes any parent
 * stacking context (modals, transformed panels, etc.) that would otherwise
 * clip or overlap it. Combined with a very high z-index this guarantees
 * the zoom popup is always on top.
 *
 * Adds Zalo-style rotation controls (90° CW / CCW) + optional save handler
 * so operators can reorient sideways CCCD scans before saving.
 */
const ZoomOverlay: React.FC<Props> = ({ src, alt, onClose, onSaveRotation, infoPanel, gallery, onActiveChange }) => {
  const [saving, setSaving] = useState(false);

  // Gallery navigation — when `gallery` is provided, derive the starting
  // index from `src` and let arrow keys / buttons walk through the list.
  const hasGallery = !!gallery && gallery.length > 1;
  const initialIndex = gallery ? Math.max(0, gallery.findIndex(g => g.src === src)) : 0;
  const [galleryIndex, setGalleryIndex] = useState(initialIndex);

  // Keep the user's chosen rotation per image across re-renders and gallery
  // navigation. Previously the effect below reset rotation every time the
  // parent re-rendered with a new `gallery` array identity (common when the
  // operator edits any info-panel field), undoing whatever rotation they
  // just applied. Storing rotation keyed by src keeps each image's
  // orientation sticky for the lifetime of this overlay.
  const rotationsRef = useRef<Map<string, number>>(new Map());
  const currentSrc = hasGallery ? gallery![galleryIndex].src : src;
  const currentLabel = hasGallery ? gallery![galleryIndex].label : undefined;
  const [rotation, setRotation] = useState<number>(
    () => rotationsRef.current.get(currentSrc) ?? 0,
  );

  // Sync rotation state with the remembered value ONLY when the displayed
  // image *actually* changes — not when the parent re-renders for an
  // unrelated reason (e.g. keystroke in the side info panel rebuilds
  // `gallery` / `onActiveChange` identities even though the image hasn't
  // moved). `lastSrcRef` guards against double-fires from React Strict
  // Mode + dep-identity churn. The effect's sole dep is `currentSrc`, and
  // the ref guard makes the logic idempotent across mount/cleanup cycles.
  const notifyRef = useRef({ hasGallery, gallery, galleryIndex, currentLabel, onActiveChange });
  useEffect(() => {
    notifyRef.current = { hasGallery, gallery, galleryIndex, currentLabel, onActiveChange };
  });
  const lastSrcRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastSrcRef.current === currentSrc) return;
    lastSrcRef.current = currentSrc;
    const storedRotation = rotationsRef.current.get(currentSrc) ?? 0;
    setRotation(r => (r === storedRotation ? r : storedRotation));
    const n = notifyRef.current;
    if (n.hasGallery && n.onActiveChange) {
      n.onActiveChange(n.gallery![n.galleryIndex]);
    } else if (n.onActiveChange) {
      n.onActiveChange({ src: currentSrc, label: n.currentLabel });
    }
  }, [currentSrc]);

  // Align galleryIndex with the caller-provided `src` when the parent
  // decides to open the overlay on a different image. Guarded by a ref so
  // repeated renders with the same `src` (gallery rebuilt on every parent
  // render) never touch galleryIndex — that would fight the user's
  // navigation via the chevron buttons.
  const lastPropSrcRef = useRef<string | null>(null);
  useEffect(() => {
    if (lastPropSrcRef.current === src) return;
    lastPropSrcRef.current = src;
    if (gallery) {
      const idx = gallery.findIndex(g => g.src === src);
      if (idx >= 0) setGalleryIndex(idx);
    }
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist any rotation change so field edits / nav round-trips keep it.
  const applyRotation = useCallback((updater: (r: number) => number) => {
    setRotation((r) => {
      const nr = updater(r);
      rotationsRef.current.set(currentSrc, nr);
      return nr;
    });
  }, [currentSrc]);

  const goPrev = useCallback(() => {
    if (!hasGallery) return;
    setGalleryIndex(i => (i - 1 + gallery!.length) % gallery!.length);
  }, [hasGallery, gallery]);

  const goNext = useCallback(() => {
    if (!hasGallery) return;
    setGalleryIndex(i => (i + 1) % gallery!.length);
  }, [hasGallery, gallery]);

  // Close on Escape, rotate on R, nav on arrow keys when gallery is active.
  //
  // ⚠ Guard against IME / text input — Vietnamese Telex produces 'r'
  // keystrokes as part of composing tone marks (e.g. 'ar' → 'ả' in some
  // mappings, or simply typing 'Mr' in a field). Without the guard, any
  // text-input keystroke that happens to include 'r' would fire rotate
  // and the image would spin. The guard also covers Esc — users in mid-
  // edit don't want a stray Esc (from their IME cancel) to close the
  // whole overlay and lose their typed draft.
  useEffect(() => {
    const isEditableTarget = (t: EventTarget | null): boolean => {
      if (!t) return false;
      const el = t as HTMLElement;
      if (!el.tagName) return false;
      const tag = el.tagName.toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;           // IME still composing — ignore
      if (isEditableTarget(e.target)) return; // typing into info panel — ignore
      if (e.key === 'Escape') onClose();
      else if (e.key === 'r' || e.key === 'R') applyRotation(r => (r + 90) % 360);
      else if (hasGallery && e.key === 'ArrowLeft') goPrev();
      else if (hasGallery && e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, hasGallery, goPrev, goNext, applyRotation]);

  const rotateCW = () => applyRotation(r => (r + 90) % 360);
  const rotateCCW = () => applyRotation(r => (r + 270) % 360);
  const reset = () => applyRotation(() => 0);

  // Render the rotated image to a canvas and hand back the bare base64
  // so the caller can persist it. Non-90° multiples would need bounding-
  // box resizing; we restrict to 90/180/270 so width↔height just swaps.
  const rotateToBase64 = useCallback((base64: string, degrees: number): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('No ctx')); return; }
        const swap = degrees === 90 || degrees === 270;
        canvas.width = swap ? img.height : img.width;
        canvas.height = swap ? img.width : img.height;
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((degrees * Math.PI) / 180);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        resolve(dataUrl.split(',')[1] || dataUrl);
      };
      img.onerror = reject;
      img.src = base64;
    }), []);

  const handleSave = async () => {
    if (!onSaveRotation || rotation === 0 || saving) return;
    setSaving(true);
    try {
      const rotated = await rotateToBase64(currentSrc, rotation);
      await onSaveRotation(rotated);
      setRotation(0);
    } catch { /* caller can toast */ }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="tt-scanner__avatar-zoom-overlay" onClick={onClose}>
      <div
        className={`tt-scanner__avatar-zoom ${infoPanel ? 'tt-scanner__avatar-zoom--with-info' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="tt-zoom-rotate-wrap"
          style={{ transform: `rotate(${rotation}deg)` }}
        >
          <ZoomableImage src={currentSrc} alt={currentLabel || alt} />
        </div>

        {hasGallery && (
          <>
            <button
              type="button"
              className="tt-zoom-nav tt-zoom-nav--prev"
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              title="Ảnh trước (←)"
              aria-label="Ảnh trước"
            >
              <i className="material-icons">chevron_left</i>
            </button>
            <button
              type="button"
              className="tt-zoom-nav tt-zoom-nav--next"
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              title="Ảnh sau (→)"
              aria-label="Ảnh sau"
            >
              <i className="material-icons">chevron_right</i>
            </button>
            <div className="tt-zoom-gallery-counter" onClick={(e) => e.stopPropagation()}>
              {currentLabel && (
                <span className="tt-zoom-gallery-counter__label">{currentLabel}</span>
              )}
              <span className="tt-zoom-gallery-counter__idx">
                {galleryIndex + 1} / {gallery!.length}
              </span>
            </div>
          </>
        )}

        {infoPanel && (
          <aside className="tt-zoom-info-panel">
            {infoPanel}
          </aside>
        )}

        <div className="tt-zoom-toolbar" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="tt-zoom-toolbar__btn"
            onClick={rotateCCW}
            title="Xoay trái 90° (R ngược)"
            aria-label="Xoay trái"
          >
            <i className="material-icons">rotate_left</i>
          </button>
          <button
            type="button"
            className="tt-zoom-toolbar__btn"
            onClick={rotateCW}
            title="Xoay phải 90° (phím R)"
            aria-label="Xoay phải"
          >
            <i className="material-icons">rotate_right</i>
          </button>
          {rotation !== 0 && (
            <button
              type="button"
              className="tt-zoom-toolbar__btn"
              onClick={reset}
              title="Về góc gốc"
              aria-label="Reset"
            >
              <i className="material-icons">restore</i>
            </button>
          )}
          {onSaveRotation && rotation !== 0 && (
            <button
              type="button"
              className="tt-zoom-toolbar__btn tt-zoom-toolbar__btn--save"
              onClick={handleSave}
              disabled={saving}
              title="Lưu ảnh đã xoay"
            >
              {saving ? <span className="tt-spinner tt-spinner--sm" /> : <i className="material-icons">save</i>}
              Lưu
            </button>
          )}
        </div>

        <button
          type="button"
          className="tt-scanner__avatar-zoom-close"
          onClick={onClose}
          aria-label="Đóng"
        >
          <i className="material-icons">close</i>
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default ZoomOverlay;
